import { useMemo } from "react";
import { createTheme, PaletteMode } from "@mui/material/styles";
import useThemeStore from "@/store/themeStore";

// Dexcom brand colors
const dexcomColors = {
  green: {
    primary: "#0db02b",
    secondary: "#137d00",
    tertiary: "#025f1d",
    // Softer variants for dark mode
    softPrimary: "#2ecc71",
    softSecondary: "#27ae60",
  },
  black: "#0f0f0f",
  white: "#ffffff",
  softWhite: "#f0f0f0",
  gray: {
    light: "#f5f5f5",
    medium: "#888888",
    dark: "#1a1a1a",
  },
};

// mui theme settings
export const themeSettings = (mode: PaletteMode) => {
  const isDark = mode === "dark";

  return {
    palette: {
      mode,
      primary: {
        main: isDark ? dexcomColors.green.softPrimary : "#137D00",
      },
      secondary: {
        main: isDark ? dexcomColors.green.softSecondary : "#EDDDFF",
      },
      error: {
        main: "#dc3545",
      },
      neutral: {
        main: isDark ? dexcomColors.gray.dark : dexcomColors.white,
      },
      info: {
        main: isDark ? dexcomColors.green.tertiary : "#CCEAE6",
      },
      background: {
        default: isDark ? dexcomColors.black : dexcomColors.white,
        paper: isDark ? dexcomColors.gray.dark : dexcomColors.white,
      },
      text: {
        primary: isDark ? dexcomColors.softWhite : "#1a1a1a",
        secondary: isDark ? dexcomColors.gray.medium : "#666666",
      },
    },
    typography: {
      fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 18,
      },
      h5: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 14,
      },
      normal: {
        fontFamily: ["Gilroy-Light", "sans-serif"].join(","),
        fontSize: 12,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? dexcomColors.black : dexcomColors.white,
            color: isDark ? dexcomColors.softWhite : "#1a1a1a",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? dexcomColors.gray.dark : dexcomColors.white,
            color: isDark ? dexcomColors.softWhite : "inherit",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? dexcomColors.gray.dark : dexcomColors.white,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(46, 204, 113, 0.12)"
                : "rgba(0, 0, 0, 0.04)",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: isDark ? dexcomColors.softWhite : "inherit",
            borderColor: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(46, 204, 113, 0.08)"
                : "rgba(0, 0, 0, 0.04)",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? dexcomColors.gray.dark : dexcomColors.white,
            color: isDark ? dexcomColors.softWhite : "inherit",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: isDark ? dexcomColors.softWhite : "inherit",
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: isDark ? dexcomColors.gray.medium : "inherit",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? dexcomColors.gray.medium : "inherit",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: isDark ? dexcomColors.softWhite : "inherit",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? dexcomColors.green.softPrimary : "#137D00",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? dexcomColors.green.softPrimary : "#137D00",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: isDark ? dexcomColors.green.softSecondary : "#137D00",
            "&:hover": {
              backgroundColor: isDark ? dexcomColors.green.softPrimary : "#0db02b",
            },
          },
          outlined: {
            borderColor: isDark ? dexcomColors.green.softPrimary : "#137D00",
            color: isDark ? dexcomColors.green.softPrimary : "#137D00",
            "&:hover": {
              borderColor: isDark ? dexcomColors.green.softPrimary : "#0db02b",
              backgroundColor: isDark ? "rgba(46, 204, 113, 0.08)" : "rgba(13, 176, 43, 0.04)",
            },
          },
        },
      },
    },
  };
};

export const useMode = () => {
  const { isDarkMode } = useThemeStore();
  const mode: PaletteMode = isDarkMode ? "dark" : "light";
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return theme;
};
